import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { bySurname } from '../lib/sort';
import { Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { studentsAPI } from '../lib/api';

/**
 * RosterAddStudentPicker — inline searchable dropdown for adding students to
 * a cycle. Cohort matches get a green pill, cross-cohort adds a gold pill.
 * Students already on the roster are hidden.
 */
export const RosterAddStudentPicker = ({ cycle, onAdd }) => {
  const [allStudents, setAllStudents] = React.useState([]);
  const [query, setQuery] = React.useState('');
  const [expanded, setExpanded] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const rosterIds = React.useMemo(() => new Set((cycle.students || []).map((s) => s.id)), [cycle.students]);

  React.useEffect(() => {
    if (!expanded || allStudents.length > 0) return;
    setLoading(true);
    studentsAPI.getAll().then((r) => {
      setAllStudents((r.data || []).filter((s) => s.status === 'active'));
    }).catch(() => {
      toast.error('Failed to load students');
    }).finally(() => setLoading(false));
  }, [expanded, allStudents.length]);

  const cohortMatches = (s) => (
    String(s.year_of_enrollment) === String(cycle.year_of_enrollment)
    && s.year_of_study === cycle.year_of_study
    && (s.course_length || '3 Year') === cycle.course_length
    && s.course_type === cycle.course_type
  );

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return allStudents
      .filter((s) => !rosterIds.has(s.id))
      .filter((s) => {
        if (!q) return true;
        return (
          (s.name || '').toLowerCase().includes(q)
          || (s.student_number || '').toLowerCase().includes(q)
          || (s.email || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        const aM = cohortMatches(a) ? 0 : 1;
        const bM = cohortMatches(b) ? 0 : 1;
        if (aM !== bM) return aM - bM;
        return bySurname(a, b);
      })
      .slice(0, 40);
  }, [allStudents, query, rosterIds]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="border border-dashed border-ica-navy/30 rounded p-3 bg-ica-navy/5" data-testid="roster-add-panel">
      {!expanded ? (
        <Button onClick={() => setExpanded(true)} data-testid="add-student-toggle" size="sm">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add student
        </Button>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-ica-navy">Add a student to this cycle</label>
            <button
              type="button"
              className="text-xs text-gray-500 hover:text-ica-navy"
              onClick={() => { setExpanded(false); setQuery(''); }}
              data-testid="add-student-cancel"
            >
              Cancel
            </button>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              autoFocus
              data-testid="add-student-search"
              placeholder="Search by name, student number or email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
          <div className="max-h-64 overflow-y-auto mt-2 divide-y bg-white rounded border" data-testid="add-student-results">
            {loading ? (
              <p className="text-xs text-gray-500 italic p-3">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-gray-500 italic p-3">
                {query ? `No unassigned students match "${query}"` : 'Every active student is already on the roster'}
              </p>
            ) : filtered.map((s) => (
              <button
                key={s.id}
                type="button"
                data-testid={`add-student-option-${s.id}`}
                onClick={() => onAdd(s.id)}
                className="w-full text-left flex items-center gap-2 px-2.5 py-1.5 hover:bg-ica-navy/5 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">{s.name}</div>
                  <div className="text-[10px] text-gray-500">
                    {s.student_number} · {s.year_of_study} · {s.course_length} · {s.course_type || '—'}
                  </div>
                </div>
                {cohortMatches(s) ? (
                  <span className="text-[9px] uppercase font-semibold bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5" title="Matches cycle cohort">cohort</span>
                ) : (
                  <span className="text-[9px] uppercase font-semibold bg-ica-gold/25 text-ica-navy rounded px-1.5 py-0.5" title="Manual — student is outside this cycle's cohort filters">cross-cohort</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RosterAddStudentPicker;
